import * as Features from '../../../../../../../shared/Features';
import * as PhotoStatus from '../../../../../../../shared/PhotoStatus';
import { AuthenticatedUser } from '../../../../../../../shared/types';
import { IOrderRow } from '../../../Entities';
import { ImageEditorPhotoStatus } from '../../entry/queries/use-query-photo-status';

export type OrderRowFileName = {
    name: string;
    orderRowId: number;
};

export enum UploadVariant {
    Edited = 'edited',
    Normal = 'normal'
}

export const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif'];

const EDITOR_UPLOAD_STATUSES = [
    PhotoStatus.UploaderApproved,
    PhotoStatus.EditorUploading,
    PhotoStatus.Se360Disapproved
];

const PHOTOGRAPHER_AS_EDITOR_UPLOAD_STATUSES = [
    PhotoStatus.Created,
    PhotoStatus.Uploading,
    PhotoStatus.UploaderApproved,
    PhotoStatus.EditorUploading,
    PhotoStatus.Se360Disapproved
];

function photographerHandlesEditing(user: AuthenticatedUser, photoStatus: ImageEditorPhotoStatus) {
    return (
        photoStatus.photographerIsEditor &&
        Features.hasFeature(Features.HandleEditedImagesWhenPhotographerIsEditor, user)
    );
}

export function canUploadEditedImages(
    user: AuthenticatedUser,
    photoStatus: ImageEditorPhotoStatus
): boolean {
    return (
        (Features.hasFeature(Features.AddEditedImages, user) &&
            EDITOR_UPLOAD_STATUSES.includes(photoStatus.currentStatus)) ||
        (photographerHandlesEditing(user, photoStatus) &&
            PHOTOGRAPHER_AS_EDITOR_UPLOAD_STATUSES.includes(photoStatus.currentStatus)) ||
        Features.hasFeature(Features.CanUploadImagesAnyTime, user)
    );
}

export function needsNormalImageUpload(
    user: AuthenticatedUser,
    photoStatus: ImageEditorPhotoStatus,
    orderRow: IOrderRow
): boolean {
    return (
        orderRow.thumbnails.length > 0 &&
        (Features.hasFeature(Features.UploadNormalImage, user) ||
            photographerHandlesEditing(user, photoStatus)) &&
        photoStatus.normalImageSizeSettings != null &&
        photoStatus.normalImageSizeSettings.autoScale === false
    );
}

export enum FileRejection {
    InvalidType = 'invalid-type',
    ExtensionMismatch = 'extension-mismatch'
}

export type FileValidation = {
    rejection: FileRejection | null;
    isUnknownOnOrder: boolean;
};

function baseName(filename: string): string {
    return filename.toLowerCase().substring(0, filename.lastIndexOf('.'));
}

function extension(filename: string): string {
    return filename.toLowerCase().substring(filename.lastIndexOf('.') + 1);
}

function isJpegVariantMismatch(uploaded: string, existing: string): boolean {
    return (
        (uploaded === 'jpeg' && existing === 'jpg') || (uploaded === 'jpg' && existing === 'jpeg')
    );
}

export function validateFile(file: File, orderRowFileNames: OrderRowFileName[]): FileValidation {
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        return { rejection: FileRejection.InvalidType, isUnknownOnOrder: false };
    }

    let matchingFileNames = orderRowFileNames.filter(
        (fileName) => baseName(fileName.name) === baseName(file.name)
    );

    if (matchingFileNames.length === 0) {
        return { rejection: null, isUnknownOnOrder: true };
    }

    let hasExtensionMismatch = matchingFileNames.some(
        (fileName) =>
            extension(fileName.name) !== extension(file.name) &&
            isJpegVariantMismatch(extension(file.name), extension(fileName.name))
    );

    return {
        rejection: hasExtensionMismatch ? FileRejection.ExtensionMismatch : null,
        isUnknownOnOrder: false
    };
}
